import { useFormikContext } from 'formik';
import { useCallback, useState } from 'react';

import {
  TableContainer,
  TableFooter,
  TableV2,
  Tbody,
  Th,
  Td,
  Thead,
  Tr,
  Button,
} from '@frontend/legacy-components';

import { NewProfitItemRow } from './NewProfitItemRow';
import {
  type EditProfitItemHandler,
  type RemoveProfitItemHandler,
  ProfitItemRow,
} from './ProfitItemRow';
import type { FormProfitItem } from './types';
import type { FormValues } from './types';

interface ProfitItemsTableProps {
  serverErrors: Map<number, string>;
  onClearServerError: (profitItemDefId: number) => void;
}

export const ProfitItemsTable = ({
  serverErrors,
  onClearServerError,
}: ProfitItemsTableProps) => {
  const { setFieldValue, values } = useFormikContext<FormValues>();

  const [isAdding, setIsAdding] = useState(false);

  const addProfitItem = useCallback(
    (profitItem: FormProfitItem) => {
      const newProfitItems = [...values.profitItems, profitItem];
      setIsAdding(false);
      setFieldValue('profitItems', newProfitItems);
    },
    [setFieldValue, values.profitItems],
  );

  const removeProfitItem = useCallback<RemoveProfitItemHandler>(
    (id) => {
      const newProfitItems = values.profitItems.filter(
        (profitItem) => profitItem.id !== id,
      );
      setFieldValue('profitItems', newProfitItems);
    },
    [setFieldValue, values.profitItems],
  );

  const editProfitItem = useCallback<EditProfitItemHandler>(
    (id, data) => {
      const newProfitItems = values.profitItems.map((profitItem) =>
        profitItem.id === id
          ? ({ ...profitItem, ...data } as FormProfitItem)
          : profitItem,
      );
      setFieldValue('profitItems', newProfitItems);

      const edited = values.profitItems.find((p) => p.id === id);
      if (edited) {
        onClearServerError(edited.profitItemDefId);
      }
    },
    [setFieldValue, values.profitItems, onClearServerError],
  );

  const startAddingProfitItem = useCallback(() => {
    setIsAdding(true);
  }, [setIsAdding]);

  const stopAddingProfitItem = useCallback(() => {
    setIsAdding(false);
  }, [setIsAdding]);

  return (
    <TableContainer flexShrink={0}>
      <TableV2>
        <Thead>
          <Tr>
            <Th>Pricing</Th>
            <Th w={24} pr={2} pl={0.25} textAlign="right">
              Percent (All Qtys)
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {!values.profitItems.length && !isAdding && (
            <Tr>
              <Td colSpan={2} textAlign="center" fontWeight="heavy">
                No pricing items
              </Td>
            </Tr>
          )}
          {values.profitItems.map((profitItem) => (
            <ProfitItemRow
              key={profitItem.id}
              profitItem={profitItem}
              onEdit={editProfitItem}
              onRemove={removeProfitItem}
              error={serverErrors.get(profitItem.profitItemDefId)}
            />
          ))}
          {isAdding && (
            <NewProfitItemRow
              onClose={stopAddingProfitItem}
              onAdd={addProfitItem}
            />
          )}
        </Tbody>
      </TableV2>
      <TableFooter bg="page.plateLight">
        <Button
          size="sm"
          isDisabled={!values.action || isAdding}
          onClick={startAddingProfitItem}
        >
          Add Pricing Item
        </Button>
      </TableFooter>
    </TableContainer>
  );
};
